import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ServiceService from '@clickersphere/core/services/ServiceService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { ServiceQueryData, SERVICES_QUERY_KEY } from './useServices';

interface IConflictPackage {
  id: string;
  name: string;
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [conflictPackages, setConflictPackages] = useState<IConflictPackage[]>(
    [],
  );

  const { isPending: deleteServiceIsPending, mutateAsync: deleteServiceAsync } =
    useMutation({
      mutationFn: (id: string) => ServiceService.delete(id),
      onMutate: async (id) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });

        const oldServices = queryClient.getQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
        );

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) => old?.filter((item) => item.id !== id),
        );

        return { oldServices };
      },
      onSuccess: () => {
        toast(t('catalog.toasts.serviceDeleted'), 'success');
      },
      onError: (error: { code?: string; details?: unknown }, _id, context) => {
        queryClient.setQueryData(
          buildQueryKey(SERVICES_QUERY_KEY),
          context?.oldServices,
        );

        if (error?.code === 'SERVICE-0002') {
          const detailsArray = Array.isArray(error.details)
            ? error.details
            : [];
          const firstDetail = detailsArray[0] as
            | { packages?: IConflictPackage[] }
            | undefined;
          const packages = firstDetail?.packages ?? [];

          if (packages.length > 0) {
            setConflictPackages(packages);
            return;
          }
        }

        toast(t('catalog.toasts.serviceDeleteError'), 'error');
      },
    });

  function clearConflictPackages() {
    setConflictPackages([]);
  }

  return {
    deleteServiceAsync,
    deleteServiceIsPending,
    conflictPackages,
    clearConflictPackages,
  };
}
